import React, { useEffect, useMemo } from "react";
import { Breadcrumb } from "~/components/Breadcrumps";
import { BreadcrumbsResourcePage } from "~/components/ResourcePage";
import { VerticalTabs, VerticalTabsLayout } from "~/components/VerticalTabs";
import { useNavigate, useParams } from "react-router-dom";
import { dictionaryCategories } from "~/modules/Dictionary/models/Dictionary";
import { dictionaryLinks } from "~/modules/Dictionary/links";
import { AccountManagersTab } from "./AccountManagersTab";
import { AgencyAccountManagersTab } from "./AgencyAccountManagersTab";
import { DspConnectionTypesTab } from "./DspConnectionTypesTab";
import { ExternalBuyersTab } from "./ExternalBuyersTab";
import { ExternalBuyersP2PTab, ExternalBuyersP2PUploadControls } from "./ExternalBuyersP2PTab";
import { PresetsTab } from "./PresetsTab";
import { SalesRepresentativesTab } from "./SalesRepresentativesTab";

type DictionaryParams = {
  [key: string]: string | undefined;
  category?: string;
};

export const DictionaryView = () => {
  const navigate = useNavigate();
  const params = useParams<DictionaryParams>();
  const categoryParam = params.category;
  const defaultCategory = dictionaryCategories[0];
  const hasValidCategory = dictionaryCategories.some((category) => category.id === categoryParam);

  useEffect(() => {
    if (!defaultCategory) {
      return;
    }
    if (!categoryParam || !hasValidCategory) {
      navigate(dictionaryLinks.category(defaultCategory.id), { replace: true });
    }
  }, [categoryParam, defaultCategory, hasValidCategory, navigate]);

  const activeCategory = useMemo(() => {
    if (!defaultCategory) {
      return null;
    }
    return dictionaryCategories.find((category) => category.id === categoryParam) ?? defaultCategory;
  }, [categoryParam, defaultCategory]);

  const categoryTabs = useMemo(
    () =>
      dictionaryCategories.map((category) => ({
        key: category.id,
        id: category.id,
        title: category.title,
        description: category.description,
      })),
    []
  );

  if (!activeCategory) {
    return null;
  }

  const categoryViews: Record<string, React.ReactNode> = {
    "dsp-connection-types": <DspConnectionTypesTab />,
    "account-managers": <AccountManagersTab />,
    "agency-managers": <AgencyAccountManagersTab />,
    "sales-representatives": <SalesRepresentativesTab />,
    "external-buyers": <ExternalBuyersTab />,
    "external-buyers-p2p": <ExternalBuyersP2PTab />,
    "presets": <PresetsTab />,
  };

  const body = categoryViews[activeCategory.id] ?? null;

  const headerToolbar =
    activeCategory?.id === "external-buyers-p2p" ? <ExternalBuyersP2PUploadControls /> : undefined;

  return (
    <Breadcrumb
      breadcrumb={{
        to: dictionaryLinks.category(activeCategory.id),
        title: "Dictionary",
      }}
    >
      <BreadcrumbsResourcePage title="Dictionary" toolbar={headerToolbar}>
        <div className="layout-content-box">
          <VerticalTabsLayout
            sidebar={
              <VerticalTabs
                items={categoryTabs}
                activeId={activeCategory.id}
                onChange={(categoryId) => navigate(dictionaryLinks.category(categoryId))}
              />
            }
          >
            <div>{body}</div>
          </VerticalTabsLayout>
        </div>
      </BreadcrumbsResourcePage>
    </Breadcrumb>
  );
};
